import { X, Plus, Minus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface CartSidebarProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

const CartSidebar = ({ isOpen, onOpenChange, children }: CartSidebarProps) => {
  const { items, updateQuantity, removeFromCart, getTotalPrice, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isCartEmpty = items.length === 0;

  const handleCheckout = () => {
    onOpenChange(false); // Close the cart
    navigate('/checkout');
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        {children}
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-md flex flex-col [&>button]:hidden">
        <SheetHeader className="flex flex-row items-center justify-between p-4 pt-2 border-b space-y-0">
          <SheetTitle className="text-lg font-semibold">Mon Panier</SheetTitle>
          <SheetClose asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Fermer le panier">
              <X className="h-4 w-4" />
            </Button>
          </SheetClose>
        </SheetHeader>
        <SheetDescription className="sr-only">
          Panier d'achat contenant vos produits sélectionnés
        </SheetDescription>

        {/* Cart content */}
        <div className="flex-1 px-4 py-2 overflow-y-auto">
          {isCartEmpty ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-muted-foreground text-xl">Votre panier est vide</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover rounded-md" />
                    ) : (
                      <span className="text-xs text-muted-foreground">Image</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm leading-tight mb-1 line-clamp-2">{item.name}</h4>
                    {item.sku && <p className="text-xs text-muted-foreground mb-1 truncate">SKU: {item.sku}</p>}
                    <p className="text-sm font-semibold">{(item.price * item.quantity).toLocaleString('fr-FR')} MAD</p>
                    {item.quantity > 1 && (
                      <p className="text-xs text-muted-foreground">{item.price.toLocaleString('fr-FR')} MAD × {item.quantity}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <span className="text-sm font-medium py-1">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => item.quantity === 1 ? removeFromCart(item.id) : updateQuantity(item.id, item.quantity - 1)}
                    >
                      {item.quantity === 1 ? (
                        <Trash2 className="h-3 w-3 text-destructive" />
                      ) : (
                        <Minus className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with total and command button */}
        {!isCartEmpty && (
          <div className="border-t p-4 space-y-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total:</span>
              <span className="font-bold text-lg">{getTotalPrice().toLocaleString('fr-FR')} MAD</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={clearCart} className="flex-1">
                Vider
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex-1">
                      <Button 
                        className="w-full" 
                        disabled={!isAuthenticated}
                        onClick={handleCheckout}
                      >
                        COMMANDER
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {!isAuthenticated && (
                    <TooltipContent>
                      <p>Vous devez vous connecter ou vous inscrire pour passer commande</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSidebar;